import { useEffect, useState, type ReactNode } from 'react';
import { api, type Folder } from '../lib/api.js';

/**
 * Choosing where an upload lands, one level at a time.
 *
 * This replaced a dropdown of every folder in the archive by full path. That
 * worked until the archive had real depth: hundreds of entries like
 * "/Launch 2026/Day 2/Pad B/Camera 3/", sorted alphabetically, with the one you
 * wanted somewhere in the middle. Browsing to a folder is how people already
 * find things in the grid, so the picker works the same way: breadcrumbs for
 * where you are, the folders inside it to go deeper, and wherever you stop is
 * the destination.
 *
 * Only the level on screen is fetched. Asking for the whole tree computes a
 * file count and size for every folder in the archive on each visit, to show
 * the handful in view.
 */

/** Past this many folders at one level, a filter box is worth its space. */
const FILTER_THRESHOLD = 12;

export function FolderPicker({
  value,
  onChange,
  canCreate,
  action = 'Uploading to',
}: {
  /** The destination folder's id, or '' for the top level. */
  value: string;
  onChange: (folderId: string) => void;
  canCreate: boolean;
  /** What happens at the destination, for the line naming it. */
  action?: string;
}): ReactNode {
  const [children, setChildren] = useState<Folder[]>([]);
  const [trail, setTrail] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Bumped after creating a folder, so the level is refetched to include it.
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    // Clicking quickly through three levels starts three fetches, and they can
    // finish in any order. Without this, a slow answer for a level already left
    // behind would overwrite the one on screen -- showing one folder's children
    // under another's breadcrumbs.
    let current = true;
    setLoading(true);
    setFilter('');
    setError(null);

    const level = api.listFolders({ parentId: value || null });
    const path = value
      ? api.getFolder(value).then((r) => [...r.ancestors, r.folder])
      : Promise.resolve<Folder[]>([]);

    Promise.all([level, path])
      .then(([listing, ancestry]) => {
        if (!current) return;
        setChildren(listing.folders);
        setTrail(ancestry);
      })
      .catch(() => {
        if (!current) return;
        // The folder named in the URL was deleted, or is one this person cannot
        // see. Staying on it would show an empty picker while uploads went to
        // an id nobody can find; the top level is at least what it says.
        if (value) onChange('');
        else setError('Could not load folders');
      })
      .finally(() => {
        if (current) setLoading(false);
      });

    return () => {
      current = false;
    };
    // onChange is deliberately left out: callers pass a fresh function each
    // render, and refetching the level for that would be wasted work.
  }, [value, revision]);

  const create = async (): Promise<void> => {
    const name = newName.trim();
    if (!name) return;
    try {
      const { folder } = await api.createFolder({ name, parentId: value || null });
      setNewName('');
      setRevision((n) => n + 1);
      // Into it, since a folder just made is almost always the one about to be
      // filled.
      onChange(folder.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create that folder');
    }
  };

  const needle = filter.trim().toLowerCase();
  const shown = needle
    ? children.filter((f) => f.name.toLowerCase().includes(needle))
    : children;

  const here = trail.at(-1)?.name ?? 'Top level';

  return (
    <div className="folder-picker" data-destination={value}>
      <nav className="folder-picker-trail" aria-label="Destination folder">
        <button
          type="button"
          className={`btn sm${value === '' ? ' current' : ''}`}
          onClick={() => onChange('')}
        >
          Top level
        </button>
        {trail.map((folder) => (
          <span key={folder.id} className="flex" style={{ gap: 4 }}>
            <span style={{ color: 'var(--text-dim)' }}>/</span>
            <button
              type="button"
              className={`btn sm${folder.id === value ? ' current' : ''}`}
              onClick={() => onChange(folder.id)}
            >
              {folder.name}
            </button>
          </span>
        ))}
      </nav>

      <p className="folder-picker-destination">
        {action} <strong>{here}</strong>
      </p>

      {error && <div className="notice error">{error}</div>}

      {children.length > FILTER_THRESHOLD && (
        <input
          className="folder-picker-filter"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={`Filter ${children.length} folders…`}
          aria-label="Filter folders"
        />
      )}

      <div className="folder-picker-list" aria-busy={loading}>
        {loading && children.length === 0 ? (
          <div className="folder-picker-empty">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="folder-picker-empty">
            {needle ? 'No folders match.' : 'No folders inside this one.'}
          </div>
        ) : (
          shown.map((folder) => (
            <button
              key={folder.id}
              type="button"
              className="folder-picker-item"
              onClick={() => onChange(folder.id)}
              title={`Open ${folder.name}`}
            >
              <span aria-hidden="true">📁</span>
              <span className="folder-picker-name">{folder.name}</span>
              {/* Says whether there is anywhere further to go before you click,
                  so a leaf is not mistaken for a level with something in it. */}
              {(folder.child_count ?? 0) > 0 && (
                <span className="folder-picker-count">
                  {folder.child_count} inside ›
                </span>
              )}
            </button>
          ))
        )}
      </div>

      {canCreate && (
        <div className="flex" style={{ marginTop: 10 }}>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create();
            }}
            placeholder={`New folder in ${here}…`}
            aria-label="New folder name"
            style={{ maxWidth: 280 }}
          />
          <button
            type="button"
            className="btn"
            onClick={() => void create()}
            disabled={!newName.trim()}
          >
            Create
          </button>
        </div>
      )}
    </div>
  );
}
